import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface LoginPageProps {}

const LoginPage: React.FC<LoginPageProps> = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Espelha ALLOWED_EMAIL_DOMAINS do backend (lista fixa no build do SPA)
  const ALLOWED_EMAIL_DOMAINS = [
    'victorsamuel.com.br',
    'centrocriativo.com',
    'pixfilmes.com',
  ];

  const isAllowedEmail = (email: string): boolean => {
    const normalized = email.trim().toLowerCase();
    const at = normalized.lastIndexOf('@');
    if (at < 0) return false;
    return ALLOWED_EMAIL_DOMAINS.includes(normalized.slice(at + 1));
  };


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Email é obrigatório');
      return;
    }

    if (!isAllowedEmail(trimmedEmail)) {
      setError('E-mail inválido para acessar o sistema');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/request-otp', {
        email: trimmedEmail
      });

      if (response.data.ok) {
        // Navigate to verify page with email parameter
        navigate(`/verify?email=${encodeURIComponent(trimmedEmail)}`);
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status) {
        setError(`Erro ao enviar código (HTTP ${err.response.status}). Tente novamente.`);
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError('Sem conexão com o servidor. Verifique a rede e tente novamente.');
      } else {
        setError('Erro ao enviar código. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-retro-bg flex items-center justify-center p-4">
      {/* Retro scanlines effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black opacity-10"></div>
        <div className="absolute inset-0" style={{
          background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 2px, transparent 4px)'
        }}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Main login card */}
        <div className="retro-card p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-retro-primary rounded-retro border-2 border-retro-line flex items-center justify-center shadow-retro-1">
              <span className="text-2xl">🎮</span>
            </div>
            <div>
              <h1 className="text-retro-pixel text-2xl text-retro-primary text-retro-glow mb-2">
                COMPRIMIR
              </h1>
              <p className="text-retro-muted text-sm">
                Sistema de Compressão Inteligente
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-retro-text mb-2">
                E-mail de acesso
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="retro-input w-full"
                placeholder="usuario@empresa.com"
                autoFocus
                disabled={loading}
              />
            </div>

            {error && (
              <div className="retro-status-error">
                <span>⚠️</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="retro-btn-primary w-full retro-press"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ENVIANDO...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  📨 ENVIAR CÓDIGO
                </span>
              )}
            </button>
          </form>

          {/* Help text */}
          <div className="text-center">
            <p className="text-xs text-retro-muted">
              💡 Aceitos: @victorsamuel.com.br, @centrocriativo.com, @pixfilmes.com
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-retro-muted">
            © 2025 | CRIÁ - Centro Criativo
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;